import { Injectable, OnDestroy, OnInit, inject } from '@angular/core';
import { FormField as FormFieldPromise } from 'textcontrol-promises-develop';
import {
  EmptyCallback,
  FieldDetails,
  FieldPositioning,
  FormField,
  InputPositionChangedEvent,
  SelectionPosition,
  TextFieldEventArgs,
} from './text-control.service.interface';
import {
  BehaviorSubject,
  Observable,
  Subscriber,
  Subscription,
  bindCallback,
  debounce,
  forkJoin,
  fromEvent,
  map,
  mergeMap,
  of,
  take,
  tap,
  timer,
  toArray,
  firstValueFrom,
  merge,
} from 'rxjs';
import { cloneDeep } from 'lodash';
import { DocumentDataService } from './document-data.service';
import { ObjectModel } from '../models/object.model';

declare const TXTextControl: any;
@Injectable()
export class TextControlFormFieldsService {
  private _cachedAllFields: FormField[] = [];
  private _cachedAllFieldHash: string = '';
  public allFields: FormField[] = [];
  public activeSelectedField: FormField | undefined;
  public currentSelectedFieldId: number | undefined;
  private subscriptions = new Subscription();
  public _activeField$: BehaviorSubject<FormField | undefined> = new BehaviorSubject<FormField | undefined>(undefined);
  private fieldObjectSubject = new BehaviorSubject<ObjectModel | undefined>(undefined)
  public fieldObject$ = this.fieldObjectSubject.asObservable();
  get activeField$(): Observable<FormField | undefined> {
    return this._activeField$;
  }

  constructor(private documentDataService: DocumentDataService) {}

  public onStart() {
    // NEVER STOP DOING THIS!
    merge(
      this.watchTextFieldEntered(),
      this.watchTextFieldLeft(),
      fromEvent(TXTextControl, 'inputPositionChanged', { capture: false })
    ).subscribe(async (results) => {
      console.debug('CMDEBUG TextField Before', results);
      const ff = await this.getCurrentFormFieldPromise();

      this.activeSelectedField = ff; // { element: element, index: details.id, total: -1, details: { ...details } };
      this.currentSelectedFieldId =
        this.activeSelectedField && this.activeSelectedField.details ? this.activeSelectedField.details.id : -1;
      // console.log('Updating selected field', this.activeSelectedField?.details.name ?? 'No Field Selected');
    });
  }

  public startNotifications() {
    console.debug('CMDEBUG TextControlFormFieldsService init');
    //this.subscriptions.add();
  }

  endNotifications() {
    this.subscriptions.unsubscribe();
  }
  public async getCurrentFormFieldPromise(): Promise<FormField | undefined> {
    return new Promise((resolve) => {
      TXTextControl.formFields.getItem(async (formField: any) => {
        if (formField) {
          const ff = await this.getFormFieldInformationPromise(formField);
          const ffResult = { element: formField, index: ff.id, total: 0, details: { ...ff } };
          this.updateCurrentFormField(ffResult);
          resolve(ffResult);
        } else {
          resolve(undefined);
        }
      });
    });
  }

  private getFormFieldInformationPromise(formField: any): Promise<any> {
    const details: any = {};
    return Promise.all([
      this.callTXTextControlCallbackPromise(formField.getName.bind(formField)).then((res: any) => {
        details.name = res;
      }),
      this.callTXTextControlCallbackPromise(formField.getText.bind(formField)).then((res: any) => {
        details.text = res;
      }),
      this.callTXTextControlCallbackPromise(formField.getID.bind(formField)).then((res: any) => {
        details.id = res;
      }),
    ]).then((res) => {
      return details;
    });
  }

  private callTXTextControlCallbackPromise(toCall: any): Promise<any> {
    return new Promise((resolve, reject) => {
      toCall((res: any) => {
        resolve(res);
      }, reject);
    });
  }

  public watchTextFieldEntered(): Observable<TextFieldEventArgs> {
    return fromEvent(TXTextControl, 'textFieldEntered', { capture: false });
  }

  public watchTextFieldLeft(): Observable<TextFieldEventArgs> {
    return fromEvent(TXTextControl, 'textFieldLeft', { capture: false });
  }
  private getFormFieldInformation(formField: any): Observable<FieldDetails> {
    return forkJoin({
      name: bindCallback(formField.getName).bind(formField)(),
      text: bindCallback(formField.getText).bind(formField)(),
      id: bindCallback(formField.getID).bind(formField)(),
    }).pipe(
      map((results): FieldDetails => {
        return {
          name: results.name.toString(),
          text: results.text.toString(),
          id: results.id,
        };
      })
    );
  }

  public getAllFormFieldsReferencesObservable(allFields: FormField[]): Observable<FormField[]> {
    return new Observable((observer) => {
      this.getTextPartsCount().then(
        (tpCount: number) => {
          const promises: Promise<FormField>[] = [];
          allFields.forEach((ff: FormField) => {
            promises.push(this.getFormFieldReferencePromise(ff, tpCount));
          });

          Promise.all(promises).then(
            (res) => {
              observer.next(res);
              observer.complete();
            },
            (error: any) => {
              observer.error(error);
            }
          );
        },
        (error: any) => observer.error(error)
      );
    });
  }

  public async getFormFieldReferencePromise(ff: FormField, textPartsCount: number = 1): Promise<FormField> {
    return new Promise(async (resolve, reject) => {
      for (let i = 0; i < textPartsCount; i++) {
        this.getTextPartAt(i).then(
          (textPart: any) => {
            textPart.formFields.getItem(
              async (e: any) => {
                if (e) {
                  let ffPromise = new FormFieldPromise(e);
                  // getstart is one based, but the rest of the code expects zero based, so we need to subtract one
                  const startpos = await ffPromise.getStart();
                  const theLength = await ffPromise.getLength();
                  ff.positioning = { start: startpos - 1, length: theLength };
                  ff.element = e;
                  resolve(ff);
                }
              },
              (err: any) => {
                reject(err);
              },
              ff.details.id
            );
          },
          (error: any) => reject(error)
        );
      }
    });
  }

  public getCurrentFormFieldObservable(): Observable<FormField | undefined> {
    return new Observable((observer) => {
      TXTextControl.formFields.getItem(
        (element: any) => {
          if (element) {
            this.getFormFieldInformation(element).subscribe((details: FieldDetails) => {
              observer.next(this.activeSelectedField);
              observer.complete();
            });
          } else {
            observer.next(undefined);
            observer.complete();
          }
        },
        (error: any) => observer.error(error)
      );
    });
  }

  public getTextPartAt(index: number): Promise<any> {
    return new Promise((resolve, reject) => {
      TXTextControl.textParts.elementAt(
        index,
        (tp: any) => resolve(tp),
        (error: any) => reject(new Error(`Failed to get text part at index ${index}: ${error.message}`))
      );
    });
  }

  public getTextPartsCount(): Promise<number> {
    return new Promise((resolve, reject) => {
      TXTextControl.textParts.getCount(
        (tpCount: number) => resolve(tpCount),
        (error: any) => reject(new Error(`Failed to get text parts count: ${error.message}`))
      );
    });
  }

  setFieldTextPromise(field: FormField, text: string, temporaryUpdate: boolean = false): Promise<string> {
    return new Promise((resolve, reject) => {
      // Asynchronous operation
      field.element.setText(
        text,
        () => {
          if (temporaryUpdate == false) {
            field.details.text = text;
          }
          resolve('Operation was successful.');
        },
        () => {
          reject('Operation failed.');
        }
      );
    });
  }

  private updateCurrentFormField(ff: FormField) {
    this._activeField$.next(ff);
    const fieldInArray = this.allFields.find((f) => f.details.id === ff.details.id);

    if (fieldInArray) {
      fieldInArray.details.text = ff.details.text;
    }
  }
}
